/**
 * Scheduled-rules admin section (ADR-0021, P4b) — the apps TIME layer UI.
 *
 * Lists a table's time-driven rules (`when` guard + `age` threshold → notify),
 * with inline enable toggle, a dry-run **preview** (how many rows match right
 * now), delete, and a create/edit dialog. A rule's config is a flat spec, so it
 * edits in a dialog.
 *
 * Recipients are membership-scoped server-side; the UI just offers the app's
 * roles. Admin console, so native <select> is allowed.
 * i18n: English fallbacks in code, Italian in it.json (repo convention).
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlarmClock, Eye, Plus, Trash2 } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { SectionHeader } from "@/components/apps/SectionHeader";
import { appsApi } from "@/lib/api-apps";
import type {
  RuleAction,
  ScheduledRuleOut,
  ScheduledRuleSpec,
  TableSummary,
} from "@/lib/api-apps";

const SEL = "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

function ageLabel(minutes: number): string {
  if (minutes % 1440 === 0) return `${minutes / 1440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}min`;
}

export function ScheduledRulesSection({
  appId,
  tables,
  canWrite,
}: {
  appId: string;
  tables: TableSummary[];
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dialog, setDialog] = React.useState<{ open: boolean; rule: ScheduledRuleOut | null }>({
    open: false,
    rule: null,
  });
  const [preview, setPreview] = React.useState<Record<string, number>>({});

  const rulesQ = useQuery({
    queryKey: ["apps", appId, "scheduled-rules"],
    queryFn: () => appsApi.listScheduledRules(appId),
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["apps", appId, "scheduled-rules"] });

  const remove = useMutation({
    mutationFn: (id: string) => appsApi.deleteScheduledRule(appId, id),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: (r: ScheduledRuleOut) =>
      appsApi.updateScheduledRule(appId, r.id, { enabled: !r.enabled }),
    onSuccess: invalidate,
  });
  const runPreview = useMutation({
    mutationFn: (id: string) => appsApi.previewScheduledRule(appId, id),
    onSuccess: (res, id) => setPreview((p) => ({ ...p, [id]: res.matched })),
  });

  const rules = rulesQ.data ?? [];
  const tableName = (id: string) => tables.find((tb) => tb.id === id)?.name ?? "—";

  return (
    <section className="space-y-2">
      <SectionHeader
        icon={AlarmClock}
        title={t("apps.scheduledRulesHeading", "Scheduled rules")}
        count={rules.length}
        action={
          canWrite && tables.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, rule: null })}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.newScheduledRule", "New rule")}
            </Button>
          ) : undefined
        }
      />

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tables.length === 0
            ? t("apps.scheduledRulesNeedTable", "Add a table first, then create a time-based rule over it.")
            : t(
                "apps.noScheduledRules",
                "No rules yet. A scheduled rule acts on rows that have sat too long (notify or transition).",
              )}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>{t("apps.scheduledRuleName", "Rule")}</Th>
                <Th className="w-28">{t("apps.scheduledRuleTable", "Table")}</Th>
                <Th className="w-24">{t("apps.scheduledRuleAge", "Age")}</Th>
                <Th className="w-28">{t("apps.scheduledRuleAction", "Action")}</Th>
                <Th className="w-20 text-center">{t("apps.scheduledRuleEnabled", "Enabled")}</Th>
                {canWrite && <Th className="w-28 text-right">{t("common.actions", "Actions")}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {rules.map((r) => (
                <Tr
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() => canWrite && setDialog({ open: true, rule: r })}
                >
                  <Td className="font-medium">{r.name}</Td>
                  <Td className="text-xs text-muted-foreground">{tableName(r.table_id)}</Td>
                  <Td className="font-mono text-xs text-muted-foreground">
                    {ageLabel(r.config.age.minutes)}
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {t("apps.scheduledRuleNotify", "Notify")}
                  </Td>
                  <Td className="text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={r.enabled}
                      aria-label={t("apps.scheduledRuleEnabled", "Enabled")}
                      disabled={!canWrite || toggle.isPending}
                      onClick={() => toggle.mutate(r)}
                      className={`inline-flex h-5 w-9 items-center rounded-full transition ${
                        r.enabled ? "bg-success" : "bg-border"
                      }`}
                    >
                      <span
                        className={`h-4 w-4 rounded-full bg-white transition ${
                          r.enabled ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </Td>
                  {canWrite && (
                    <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {preview[r.id] !== undefined && (
                          <span className="mr-1 text-xs text-muted-foreground">
                            {`${preview[r.id]} ${t("apps.scheduledRuleMatchUnit", "match")}`}
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("apps.scheduledRulePreview", "Preview")}
                          disabled={runPreview.isPending}
                          onClick={() => runPreview.mutate(r.id)}
                        >
                          <Eye className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.delete", "Delete")}
                          onClick={() => {
                            if (
                              confirm(
                                t("apps.confirmDeleteScheduledRule", 'Delete rule "{{name}}"?', {
                                  name: r.name,
                                }),
                              )
                            ) {
                              remove.mutate(r.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}

      {dialog.open && (
        <ScheduledRuleDialog
          appId={appId}
          tables={tables}
          rule={dialog.rule}
          open={dialog.open}
          onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        />
      )}
    </section>
  );
}

const DEFAULT_SPEC: ScheduledRuleSpec = {
  when: "",
  age: { column: "updated_at", minutes: 1440 },
  action: { type: "notify", title: "", notify_members: true },
};

export function ScheduledRuleDialog({
  appId,
  tables,
  rule,
  open,
  onOpenChange,
}: {
  appId: string;
  tables: TableSummary[];
  rule: ScheduledRuleOut | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const editing = !!rule;

  const [name, setName] = React.useState(rule?.name ?? "");
  const [tableId, setTableId] = React.useState(rule?.table_id ?? "");
  const [spec, setSpec] = React.useState<ScheduledRuleSpec>(rule?.config ?? DEFAULT_SPEC);

  const rolesQ = useQuery({ queryKey: ["apps", appId, "roles"], queryFn: () => appsApi.listRoles(appId) });
  const roles = rolesQ.data ?? [];

  const setAction = (a: RuleAction) => setSpec((s) => ({ ...s, action: a }));

  const save = useMutation({
    mutationFn: () => {
      const config: ScheduledRuleSpec = { ...spec, when: spec.when?.trim() || null };
      if (editing && rule) return appsApi.updateScheduledRule(appId, rule.id, { name: name.trim(), config });
      return appsApi.createScheduledRule(appId, { name: name.trim(), table_id: tableId, config });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "scheduled-rules"] });
      onOpenChange(false);
    },
  });

  const action = spec.action;
  const notifyValid =
    action.title.trim().length > 0 &&
    (action.notify_members || action.notify_owner || (action.roles?.length ?? 0) > 0);
  const canSubmit =
    name.trim().length > 0 && !!tableId && notifyValid && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t("apps.editScheduledRule", "Edit rule")
              : t("apps.newScheduledRule", "New rule")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="sr-name">{t("apps.scheduledRuleName", "Rule")}</Label>
            <Input id="sr-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {!editing && (
            <div className="space-y-1">
              <Label htmlFor="sr-table">{t("apps.scheduledRuleTable", "Table")}</Label>
              <select
                id="sr-table"
                className={SEL}
                value={tableId}
                onChange={(e) => setTableId(e.target.value)}
              >
                <option value="">{t("common.choose", "Choose…")}</option>
                {tables.map((tb) => (
                  <option key={tb.id} value={tb.id}>
                    {tb.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* age */}
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="sr-age-col">{t("apps.scheduledRuleAgeColumn", "Since")}</Label>
              <select
                id="sr-age-col"
                className={SEL}
                value={spec.age.column}
                onChange={(e) =>
                  setSpec((s) => ({
                    ...s,
                    age: { ...s.age, column: e.target.value as "created_at" | "updated_at" },
                  }))
                }
              >
                <option value="updated_at">{t("apps.scheduledRuleAgeUpdated", "Last change")}</option>
                <option value="created_at">{t("apps.scheduledRuleAgeCreated", "Creation")}</option>
              </select>
            </div>
            <div className="w-28 space-y-1">
              <Label htmlFor="sr-age-min">{t("apps.scheduledRuleAgeMinutes", "Minutes")}</Label>
              <Input
                id="sr-age-min"
                type="number"
                min={1}
                value={spec.age.minutes}
                onChange={(e) =>
                  setSpec((s) => ({ ...s, age: { ...s.age, minutes: Number(e.target.value) || 1 } }))
                }
              />
            </div>
          </div>

          {/* when guard */}
          <div className="space-y-1">
            <Label htmlFor="sr-when">{t("apps.scheduledRuleWhen", "Condition (JS, optional)")}</Label>
            <Input
              id="sr-when"
              className="font-mono text-xs"
              placeholder="$json.stage === 'pending'"
              value={spec.when ?? ""}
              onChange={(e) => setSpec((s) => ({ ...s, when: e.target.value }))}
            />
          </div>

          <NotifyFields action={action} roles={roles} onChange={setAction} />

          {save.isError && (
            <Alert tone="danger" title={t("apps.scheduledRuleSaveFailed", "Could not save")}>
              {String((save.error as Error)?.message ?? "")}
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => save.mutate()}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotifyFields({
  action,
  roles,
  onChange,
}: {
  action: Extract<RuleAction, { type: "notify" }>;
  roles: { slug: string; name: string }[];
  onChange: (a: RuleAction) => void;
}) {
  const { t } = useTranslation();
  const toggleRole = (slug: string) => {
    const cur = new Set(action.roles ?? []);
    if (cur.has(slug)) cur.delete(slug);
    else cur.add(slug);
    onChange({ ...action, roles: [...cur] });
  };
  return (
    <div className="space-y-3 rounded-md border border-border/60 p-3">
      <div className="space-y-1">
        <Label htmlFor="sr-title">{t("apps.scheduledRuleTitle", "Title")}</Label>
        <Input
          id="sr-title"
          value={action.title}
          onChange={(e) => onChange({ ...action, title: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="sr-body">{t("apps.scheduledRuleBody", "Body (optional)")}</Label>
        <Input
          id="sr-body"
          value={action.body ?? ""}
          onChange={(e) => onChange({ ...action, body: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("apps.scheduledRuleRecipients", "Recipients (app members)")}</Label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!action.notify_members}
            onChange={(e) => onChange({ ...action, notify_members: e.target.checked })}
          />
          {t("apps.scheduledRuleNotifyMembers", "All members")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!action.notify_owner}
            onChange={(e) => onChange({ ...action, notify_owner: e.target.checked })}
          />
          {t("apps.scheduledRuleNotifyOwner", "Row owner")}
        </label>
        {roles.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {roles.map((r) => (
              <label key={r.slug} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={(action.roles ?? []).includes(r.slug)}
                  onChange={() => toggleRole(r.slug)}
                />
                {r.name}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
